import { useState } from "react";
import { BrandLogo } from "../ui/BrandLogo.js";
import {
  categoryLabels,
  centsToDollars,
  dollarsToCents,
  propertyTypeLabels,
  urgencyLabels,
  type AttachedBinderEntryView,
  type JobStatusUpdateBody,
  type JobView,
  type OpportunityView,
  type PropertyInsightsView,
  type SubmitQuoteBody,
  type ThreadMessage,
} from "./api.js";

/**
 * The opportunity detail body (unit 10), extracted in unit 17 so the
 * magic-link page and the signed-in portal render ONE component over two
 * data sources. Callers own the data fetching and pass mutation-shaped
 * actions in; react-query mutation results satisfy `ActionState`
 * structurally.
 *
 * `questionAction` is optional: the pre-bid Q&A composer is a magic-link
 * affordance (unit 9's token-authenticated route). The portal still shows
 * the shared answered-questions list and any pending question.
 */
export interface ActionState<TVars> {
  mutate: (vars: TVars, options?: { onSuccess?: () => void }) => void;
  isPending: boolean;
  error: Error | null;
}

export interface ThreadState {
  threadOpen: boolean;
  messages: ThreadMessage[];
  send: ActionState<string>;
}

export function OpportunityDetailBody({
  view,
  brandHeader,
  quoteAction,
  thread,
  questionAction,
  jobAction,
  quoteCtaLabel = "Send quote",
}: {
  view: OpportunityView;
  /** The standalone magic-link page shows the brand header; the portal shell already has one. */
  brandHeader: boolean;
  quoteAction: ActionState<SubmitQuoteBody>;
  thread: ThreadState;
  questionAction?: ActionState<string>;
  /** Job lifecycle controls (unit 18) — only the selection winner has `view.job`. */
  jobAction?: ActionState<JobStatusUpdateBody>;
  /** Public page overrides with its "no account needed" reassurance. */
  quoteCtaLabel?: string;
}) {
  const opp = view.opportunity;
  const photoEntries = Object.entries(opp.photos);

  return (
    <main className="mx-auto max-w-md pb-16">
      <header className="border-b border-charcoal/15 bg-white px-4 py-5">
        {brandHeader && <BrandLogo className="mb-4" />}
        <h1 className="mt-2 text-2xl font-bold tracking-tight text-charcoal">
          {categoryLabels[opp.serviceCategory] ?? opp.serviceCategory} request
        </h1>
        <p className="mt-1 text-sm text-charcoal">
          {opp.customerFirstName} · {opp.location.city}, {opp.location.state}{" "}
          {opp.location.postalCode}
        </p>
        <p className="mt-1 text-sm font-medium text-amber-700">
          {urgencyLabels[opp.urgency] ?? opp.urgency}
        </p>
      </header>

      <div className="space-y-7 px-4 py-6">
        <section>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-charcoal">
            What they need
          </h2>
          <p className="mt-2 whitespace-pre-wrap text-[15px] font-medium leading-relaxed text-charcoal">
            {opp.description}
          </p>
        </section>

        {photoEntries.length > 0 && (
          <section>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-charcoal">Photos</h2>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {photoEntries.map(([id, url]) => (
                <a key={id} href={url} target="_blank" rel="noreferrer">
                  <img
                    src={url}
                    alt="Job photo"
                    className="aspect-square w-full rounded-xl object-cover"
                  />
                </a>
              ))}
            </div>
          </section>
        )}

        {(opp.attachedBinder?.length ?? 0) > 0 && (
          <PropertyFileSection entries={opp.attachedBinder!} />
        )}

        {opp.propertyInsights && <PropertyInsightsSection insights={opp.propertyInsights} />}

        {view.job && jobAction && <JobSection job={view.job} jobAction={jobAction} />}
        <QASection view={view} questionAction={questionAction} />
        {/* The winner's quote is settled — hide the revise form once a job exists. */}
        {!view.job && (
          <QuoteSection view={view} quoteAction={quoteAction} ctaLabel={quoteCtaLabel} />
        )}
        <MessagesSection thread={thread} />
      </div>
    </main>
  );
}

/**
 * Property File the customer chose to share with this request (read-only). Each
 * entry shows its facts, notes, and any photos (presigned URLs from
 * media-release). Helps a pro scope/price before quoting.
 */
function PropertyFileSection({ entries }: { entries: AttachedBinderEntryView[] }) {
  return (
    <section>
      <h2 className="text-xs font-semibold uppercase tracking-wide text-charcoal">
        Property details they shared
      </h2>
      <div className="mt-2 space-y-3">
        {entries.map((e) => {
          const facts = Object.entries(e.facts ?? {});
          const photos = Object.entries(e.photos);
          return (
            <div key={e.entryId} className="rounded-2xl border border-charcoal/15 bg-white p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-charcoal">{e.title}</span>
                {e.locationLabel && (
                  <span className="rounded-full bg-field-gray px-2 py-0.5 text-xs text-charcoal">
                    {e.locationLabel}
                  </span>
                )}
              </div>
              {facts.length > 0 && (
                <dl className="mt-2 space-y-0.5">
                  {facts.map(([key, f]) => (
                    <div key={key} className="flex justify-between gap-3 text-sm">
                      <dt className="text-charcoal">{f.label}</dt>
                      <dd className="font-medium text-charcoal">
                        {typeof f.value === "boolean" ? (f.value ? "Yes" : "No") : String(f.value)}
                        {f.unit ? ` ${f.unit}` : ""}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
              {e.notes && (
                <p className="mt-2 whitespace-pre-wrap text-sm text-charcoal">{e.notes}</p>
              )}
              {photos.length > 0 && (
                <div className="mt-2 grid grid-cols-4 gap-2">
                  {photos.map(([id, url]) => (
                    <a key={id} href={url} target="_blank" rel="noreferrer">
                      <img
                        src={url}
                        alt="Shared property photo"
                        className="aspect-square w-full rounded-lg object-cover"
                      />
                    </a>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/**
 * Customer-shared Property Insights (rounded upstream). Every figure is an
 * estimate for scoping — the "~" prefix keeps that honest. Renders nothing
 * when no shareable field is present.
 */
function PropertyInsightsSection({ insights }: { insights: PropertyInsightsView }) {
  const fmt = (n: number) => n.toLocaleString();
  const rows: { label: string; value: string }[] = [
    ...(insights.propertyType
      ? [
          {
            label: "Property type",
            value: propertyTypeLabels[insights.propertyType] ?? insights.propertyType,
          },
        ]
      : []),
    ...(insights.lotSizeSqft !== undefined
      ? [{ label: "Lot size", value: `~${fmt(insights.lotSizeSqft)} sq ft` }]
      : []),
    ...(insights.homeSqft !== undefined
      ? [{ label: "Home size", value: `~${fmt(insights.homeSqft)} sq ft` }]
      : []),
    ...(insights.mowableAreaSqft !== undefined
      ? [{ label: "Mowable lawn area", value: `~${fmt(insights.mowableAreaSqft)} sq ft` }]
      : []),
    ...(insights.yearBuilt !== undefined
      ? [{ label: "Year built", value: String(insights.yearBuilt) }]
      : []),
  ];
  if (rows.length === 0) return null;
  return (
    <section>
      <h2 className="text-xs font-semibold uppercase tracking-wide text-charcoal">
        Property details (customer-shared)
      </h2>
      <div className="mt-2 rounded-2xl border border-charcoal/15 bg-white p-3">
        <dl className="space-y-0.5">
          {rows.map((r) => (
            <div key={r.label} className="flex justify-between gap-3 text-sm">
              <dt className="text-charcoal">{r.label}</dt>
              <dd className="font-medium text-charcoal">{r.value}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-2 text-xs text-charcoal">
          Automatically estimated from public records and aerial imagery — verify on site.
        </p>
      </div>
    </section>
  );
}

const jobStatusLabels: Record<JobView["status"], string> = {
  SELECTED: "You got the job!",
  ACCEPTED: "Job accepted",
  SCHEDULED: "Scheduled",
  IN_PROGRESS: "Work in progress",
  COMPLETED: "Job complete",
  CANCELLED: "Job cancelled",
};

/**
 * Job lifecycle controls (unit 18). Every change here streams to the
 * customer's status timeline, and ACCEPTED/SCHEDULED/COMPLETED email them.
 */
function JobSection({
  job,
  jobAction,
}: {
  job: JobView;
  jobAction: ActionState<JobStatusUpdateBody>;
}) {
  const [date, setDate] = useState(job.scheduledFor ?? "");
  const [scheduling, setScheduling] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  const terminal = job.status === "COMPLETED" || job.status === "CANCELLED";
  const update = (body: JobStatusUpdateBody) =>
    jobAction.mutate(body, {
      onSuccess: () => {
        setScheduling(false);
        setConfirmingCancel(false);
      },
    });

  return (
    <section>
      <h2 className="text-xs font-semibold uppercase tracking-wide text-charcoal">Your job</h2>
      <div className="mt-2 rounded-2xl border border-brand-200 bg-brand-50 p-4">
        <p className="text-lg font-bold text-brand-900">{jobStatusLabels[job.status]}</p>
        {job.scheduledFor && job.status !== "CANCELLED" && (
          <p className="mt-0.5 text-sm text-brand-900">Work date: {job.scheduledFor}</p>
        )}
        {job.status === "SELECTED" && (
          <p className="mt-1 text-sm text-brand-900">
            The customer picked your quote — their contact details are in your email. Let them know
            you&apos;re taking the job.
          </p>
        )}

        {!terminal && (
          <div className="mt-3 flex flex-wrap gap-2">
            {job.status === "SELECTED" && (
              <button
                type="button"
                disabled={jobAction.isPending}
                onClick={() => update({ status: "ACCEPTED" })}
                className="min-h-[44px] rounded-xl bg-brand-700 px-4 text-sm font-semibold text-white disabled:opacity-50"
              >
                Accept job
              </button>
            )}
            {(job.status === "ACCEPTED" || job.status === "SCHEDULED") && (
              <button
                type="button"
                onClick={() => setScheduling((v) => !v)}
                className="min-h-[44px] rounded-xl border border-brand-700 px-4 text-sm font-semibold text-brand-700"
              >
                {job.status === "SCHEDULED" ? "Reschedule" : "Set work date"}
              </button>
            )}
            {(job.status === "ACCEPTED" || job.status === "SCHEDULED") && (
              <button
                type="button"
                disabled={jobAction.isPending}
                onClick={() => update({ status: "IN_PROGRESS" })}
                className="min-h-[44px] rounded-xl border border-brand-700 px-4 text-sm font-semibold text-brand-700 disabled:opacity-50"
              >
                Start work
              </button>
            )}
            {job.status !== "SELECTED" && (
              <button
                type="button"
                disabled={jobAction.isPending}
                onClick={() => update({ status: "COMPLETED" })}
                className="min-h-[44px] rounded-xl bg-brand-700 px-4 text-sm font-semibold text-white disabled:opacity-50"
              >
                Mark complete
              </button>
            )}
          </div>
        )}

        {scheduling && !terminal && (
          <form
            className="mt-3 flex items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (date) update({ status: "SCHEDULED", scheduledFor: date });
            }}
          >
            <label className="block flex-1">
              <span className="text-sm font-medium text-brand-900">Work date</span>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                className="mt-1 w-full rounded-xl border border-charcoal/30 bg-white p-2.5 text-base"
              />
            </label>
            <button
              type="submit"
              disabled={jobAction.isPending || !date}
              className="min-h-[44px] rounded-xl bg-brand-700 px-4 text-sm font-semibold text-white disabled:opacity-50"
            >
              Confirm
            </button>
          </form>
        )}

        {!terminal && (
          <div className="mt-3">
            {confirmingCancel ? (
              <div className="space-y-2 rounded-xl bg-white p-3">
                <p className="text-sm text-charcoal">
                  Cancel this job? The customer will be told and can pick another pro.
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={jobAction.isPending}
                    onClick={() => update({ status: "CANCELLED" })}
                    className="min-h-[44px] rounded-xl bg-rose-600 px-4 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    Yes, cancel the job
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmingCancel(false)}
                    className="min-h-[44px] rounded-xl border border-charcoal/30 px-4 text-sm font-semibold text-charcoal"
                  >
                    Keep the job
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmingCancel(true)}
                className="text-sm font-medium text-charcoal underline"
              >
                I can&apos;t do this job
              </button>
            )}
          </div>
        )}

        {jobAction.error && <p className="mt-2 text-sm text-rose-700">{jobAction.error.message}</p>}
      </div>
    </section>
  );
}

function MessagesSection({ thread }: { thread: ThreadState }) {
  const [draft, setDraft] = useState("");
  const { messages, threadOpen, send } = thread;

  return (
    <section>
      <h2 className="text-xs font-semibold uppercase tracking-wide text-charcoal">
        Messages with the customer
      </h2>
      {messages.length > 0 && (
        <div className="mt-2 space-y-2">
          {messages.map((m) => (
            <div
              key={m.messageId}
              className={`max-w-[85%] rounded-2xl p-3 text-[15px] leading-relaxed ${
                m.sender === "contractor"
                  ? "ml-auto bg-brand-700 text-white"
                  : "bg-white text-charcoal ring-1 ring-charcoal/15"
              }`}
            >
              <p className="whitespace-pre-wrap">{m.body}</p>
              <p
                className={`mt-1 text-[11px] ${
                  m.sender === "contractor" ? "text-brand-100" : "text-charcoal"
                }`}
              >
                {new Date(m.sentAt).toLocaleString()}
              </p>
            </div>
          ))}
        </div>
      )}
      {threadOpen ? (
        <form
          className="mt-2 space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            const body = draft.trim();
            if (body.length === 0) return;
            send.mutate(body, { onSuccess: () => setDraft("") });
          }}
        >
          <textarea
            className="w-full rounded-xl border border-charcoal/30 p-2.5 text-[15px]"
            rows={2}
            maxLength={2000}
            placeholder={
              messages.length === 0
                ? "Message the customer about scheduling, access, details..."
                : "Reply..."
            }
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className="flex items-center justify-end">
            <button
              type="submit"
              disabled={send.isPending || draft.trim().length === 0}
              className="rounded-xl bg-brand-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {send.isPending ? "Sending..." : "Send"}
            </button>
          </div>
          {send.error && <p className="text-sm text-rose-700">{send.error.message}</p>}
        </form>
      ) : (
        messages.length > 0 && (
          <p className="mt-2 text-sm text-charcoal">This conversation is closed.</p>
        )
      )}
    </section>
  );
}

function QASection({
  view,
  questionAction,
}: {
  view: OpportunityView;
  questionAction?: ActionState<string>;
}) {
  const [draft, setDraft] = useState("");
  const pendingUnanswered = view.myQuestion !== null && !view.myQuestion.answered;
  const hasContent =
    view.opportunity.answeredQuestions.length > 0 || pendingUnanswered || questionAction;

  if (!hasContent) return null;

  return (
    <section>
      <h2 className="text-xs font-semibold uppercase tracking-wide text-charcoal">
        Questions &amp; answers
      </h2>

      {view.opportunity.answeredQuestions.length > 0 && (
        <div className="mt-2 space-y-2">
          {view.opportunity.answeredQuestions.map((q) => (
            <div key={q.questionId} className="rounded-2xl border border-charcoal/15 bg-white p-3">
              <p className="text-sm font-medium text-charcoal">{q.question}</p>
              <p className="mt-1.5 whitespace-pre-wrap rounded-xl bg-brand-50 p-2.5 text-sm leading-relaxed text-brand-900">
                {q.answer}
              </p>
            </div>
          ))}
        </div>
      )}

      {pendingUnanswered ? (
        <p className="mt-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          Your question — &ldquo;{view.myQuestion!.question}&rdquo; — was sent to the customer.
          You&apos;ll see the answer here.
        </p>
      ) : (
        questionAction && (
          <form
            className="mt-2 space-y-2"
            onSubmit={(e) => {
              e.preventDefault();
              const question = draft.trim();
              if (question.length < 5) return;
              questionAction.mutate(question, { onSuccess: () => setDraft("") });
            }}
          >
            <textarea
              className="w-full rounded-xl border border-charcoal/30 p-2.5 text-[15px]"
              rows={2}
              maxLength={500}
              placeholder="Need to know something before you quote? Ask here..."
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-charcoal">Answers are shared with all invited pros.</p>
              <button
                type="submit"
                disabled={questionAction.isPending || draft.trim().length < 5}
                className="rounded-xl bg-field-gray px-3 py-2 text-sm font-semibold text-charcoal disabled:opacity-50"
              >
                {questionAction.isPending ? "Sending..." : "Ask"}
              </button>
            </div>
            {questionAction.error && (
              <p className="text-sm text-rose-700">{questionAction.error.message}</p>
            )}
          </form>
        )
      )}
    </section>
  );
}

type QuoteType = "fixed" | "range" | "needs_visit";

function QuoteSection({
  view,
  quoteAction,
  ctaLabel,
}: {
  view: OpportunityView;
  quoteAction: ActionState<SubmitQuoteBody>;
  ctaLabel: string;
}) {
  const existing = view.quote;
  const [editing, setEditing] = useState(existing === null);
  const [type, setType] = useState<QuoteType>(existing?.type ?? "fixed");
  const [amount, setAmount] = useState(
    existing?.amount ? (existing.amount.amountCents / 100).toFixed(2) : "",
  );
  const [minAmount, setMinAmount] = useState(
    existing?.minAmount ? (existing.minAmount.amountCents / 100).toFixed(2) : "",
  );
  const [maxAmount, setMaxAmount] = useState(
    existing?.maxAmount ? (existing.maxAmount.amountCents / 100).toFixed(2) : "",
  );
  const [notes, setNotes] = useState(existing?.notes ?? "");
  const [formError, setFormError] = useState<string | null>(null);

  if (existing && !editing) {
    return (
      <section>
        <h2 className="text-xs font-semibold uppercase tracking-wide text-charcoal">Your quote</h2>
        <div className="mt-2 rounded-2xl border border-brand-200 bg-brand-50 p-4">
          <p className="text-lg font-bold text-brand-900">
            {existing.type === "fixed" &&
              existing.amount &&
              centsToDollars(existing.amount.amountCents)}
            {existing.type === "range" &&
              existing.minAmount &&
              existing.maxAmount &&
              `${centsToDollars(existing.minAmount.amountCents)} – ${centsToDollars(existing.maxAmount.amountCents)}`}
            {existing.type === "needs_visit" && "Site visit needed to price"}
          </p>
          {existing.notes && <p className="mt-1 text-sm text-brand-900">{existing.notes}</p>}
          <p className="mt-2 text-xs text-brand-800">
            Sent {new Date(existing.submittedAt).toLocaleString()}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="mt-2 text-sm font-semibold text-brand-700 underline"
        >
          Revise quote
        </button>
      </section>
    );
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    let body: SubmitQuoteBody;
    const shared = { ...(notes.trim() ? { notes: notes.trim() } : {}) };
    if (type === "fixed") {
      const cents = dollarsToCents(amount);
      if (!cents) return setFormError("Enter a valid price, like 425 or 425.50");
      body = { type, amount: { currency: "USD", amountCents: cents }, ...shared };
    } else if (type === "range") {
      const min = dollarsToCents(minAmount);
      const max = dollarsToCents(maxAmount);
      if (!min || !max) return setFormError("Enter valid low and high prices");
      if (min > max) return setFormError("The low end can't be more than the high end");
      body = {
        type,
        minAmount: { currency: "USD", amountCents: min },
        maxAmount: { currency: "USD", amountCents: max },
        ...shared,
      };
    } else {
      body = { type, ...shared };
    }
    quoteAction.mutate(body, { onSuccess: () => setEditing(false) });
  }

  return (
    <section>
      <h2 className="text-xs font-semibold uppercase tracking-wide text-charcoal">
        {existing ? "Revise your quote" : "Send your quote"}
      </h2>
      <form className="mt-2 space-y-3" onSubmit={submit}>
        <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Quote type">
          {(
            [
              ["fixed", "Fixed price"],
              ["range", "Price range"],
              ["needs_visit", "Need to see it"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={type === value}
              onClick={() => setType(value)}
              className={`rounded-xl border p-2.5 text-sm font-semibold ${
                type === value
                  ? "border-brand-700 bg-brand-50 text-brand-800"
                  : "border-charcoal/30 bg-white text-charcoal"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {type === "fixed" && (
          <label className="block">
            <span className="text-sm font-medium text-charcoal">Your price</span>
            <input
              inputMode="decimal"
              placeholder="425.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1 w-full rounded-xl border border-charcoal/30 p-3 text-base"
            />
          </label>
        )}
        {type === "range" && (
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-sm font-medium text-charcoal">From</span>
              <input
                inputMode="decimal"
                placeholder="300"
                value={minAmount}
                onChange={(e) => setMinAmount(e.target.value)}
                className="mt-1 w-full rounded-xl border border-charcoal/30 p-3 text-base"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-charcoal">To</span>
              <input
                inputMode="decimal"
                placeholder="550"
                value={maxAmount}
                onChange={(e) => setMaxAmount(e.target.value)}
                className="mt-1 w-full rounded-xl border border-charcoal/30 p-3 text-base"
              />
            </label>
          </div>
        )}
        {type === "needs_visit" && (
          <p className="rounded-2xl bg-field-gray p-3 text-sm text-charcoal">
            The customer will see that you&apos;d like to look at the job before pricing it. Add a
            note about when you could stop by.
          </p>
        )}

        <label className="block">
          <span className="text-sm font-medium text-charcoal">Notes (optional)</span>
          <textarea
            rows={3}
            maxLength={2000}
            placeholder="What's included, when you could start..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-1 w-full rounded-xl border border-charcoal/30 p-3 text-base"
          />
        </label>

        {(formError || quoteAction.error) && (
          <p className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
            {formError ?? quoteAction.error?.message}
          </p>
        )}

        <button
          type="submit"
          disabled={quoteAction.isPending}
          className="min-h-[48px] w-full rounded-xl bg-brand-700 text-base font-semibold text-white disabled:opacity-50"
        >
          {quoteAction.isPending ? "Sending..." : existing ? "Update quote" : ctaLabel}
        </button>
      </form>
    </section>
  );
}
